import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ToastService } from '../../../shared/services/toast.service';
import { DatetimeService } from '../../../shared/services/datetime.service';

import { EmailMessageApiService } from '../../correos/services/email-message-api.service';
import {
  ContactInboxSyncResponse,
  EmailMessageDetailResponse,
  InboxMessageDetailResponse,
  MyTimelineSearchParams,
  TimelineItemResponse
} from '../../correos/models/email-message.model';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { Contact } from '../../contactos/models/contacto.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { EmailComposeService, EmailComposeOptions } from '../../correos/services/email-compose.service';

/** Filtro de dirección de la bandeja unificada. */
type DirectionFilter = 'ALL' | 'RECEIVED' | 'SENT';

/**
 * View-model unificado de un item de la bandeja. Mapea un TimelineItemResponse
 * (fuente única: GET /api/emails/timeline/mine, ya fusionado y ordenado a nivel
 * BD) a una fila renderizable. Conserva `direction` y `sourceType`/`sourceId`
 * para rutear el detalle del panel derecho.
 */
interface InboxItem {
  direction: 'SENT' | 'RECEIVED';
  sourceType: 'EMAIL_MESSAGE' | 'INBOX_MESSAGE';
  sourceId: number;
  /** clave única estable para *ngFor track. */
  key: string;
  fecha: string | null;        // occurredAt
  subject: string | null;
  /** Persona del otro lado: remitente (recibido) o destinatario (enviado). */
  counterpartName: string | null;
  counterpartEmail: string | null;
  /** Solo significativo en recibidos (enviados siempre "leído"). */
  read: boolean;
  status: string | null;       // solo enviados (QUEUED/SENT/FAILED/...)
  /** Solo enviados: nombre del enum EmailPurpose; null en recibidos. */
  purpose: string | null;
  /** Solo enviados: true = campaña/masivo, false = personal; null en recibidos. */
  bulk: boolean | null;
}

const PAGE_SIZE = 20;

@Component({
  selector: 'app-inbox-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule
  ],
  templateUrl: './inbox-list.component.html'
})
export class InboxListComponent implements OnInit, OnDestroy {

  // ── Lista unificada (fuente única: timeline/mine) ──
  items: InboxItem[] = [];
  loading = false;       // carga inicial / cambio de filtros
  loadingMore = false;   // "Cargar más"
  errorMessage = '';

  // Paginación simple (page/size). El botón "Cargar más" hace page+1.
  private page = 0;
  private last = false;

  /** Sync IMAP en segundo plano (no bloquea la lista). */
  syncing = false;
  /** El usuario no tiene IMAP conectado → aviso no intrusivo con CTA a configuración. */
  noInbox = false;

  // ── Filtros (server-side) ──
  filtroQ = '';
  soloNoLeidos = false;
  directionFilter: DirectionFilter = 'ALL';

  // ── Filtro "Conversación con" (contacto o correo) ──
  /** Si hay conversación activa: contactId (preferente) o email de la contraparte. */
  convContactId: number | null = null;
  convEmail: string | null = null;
  /** Etiqueta a mostrar en el chip "Conversación con: <X>". */
  convLabel: string | null = null;

  // Buscador de contactos (dropdown).
  showContactSearch = false;
  contactQuery = '';
  contactResults: Contact[] = [];
  contactSearching = false;
  private readonly contactSearch$ = new Subject<string>();

  // ── Detalle (panel derecho) ──
  selectedKey: string | null = null;
  selectedDirection: 'SENT' | 'RECEIVED' | null = null;
  detailLoading = false;
  detailError = '';

  // Detalle RECIBIDO (caché BD) y ENVIADO (outbox) — solo uno activo a la vez.
  receivedDetail: InboxMessageDetailResponse | null = null;
  sentDetail: EmailMessageDetailResponse | null = null;

  /**
   * El cuerpo HTML (de cualquier dirección) viene de fuente no confiable. Se
   * renderiza en un <iframe sandbox> SIN `allow-scripts`: el navegador aísla
   * el contenido y NO ejecuta scripts del correo. La fuente es un blob URL
   * local (text/html) marcado como recurso seguro.
   */
  bodyFrameSrc: SafeResourceUrl | null = null;
  private bodyBlobUrl: string | null = null;

  private readonly searchSubject = new Subject<void>();
  private readonly subscription = new Subscription();

  constructor(
    private emailApi: EmailMessageApiService,
    private contactoApi: ContactoApiService,
    private authService: AuthService,
    private toast: ToastService,
    private datetimeService: DatetimeService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private emailCompose: EmailComposeService
  ) {}

  irAConfiguracion(): void {
    this.router.navigate(['/correo/configuracion']);
  }

  /** Abre el compose flotante global de redacción de correo suelto. */
  abrirRedactar(): void {
    this.emailCompose.open();
  }

  /** Tras enviar con éxito: el correo aparece en enviados → refrescar la bandeja. */
  onCorreoEnviado(): void {
    this.recargar();
  }

  ngOnInit(): void {
    // Búsqueda con debounce: cada cambio recarga desde page 0.
    this.subscription.add(
      this.searchSubject.pipe(debounceTime(450)).subscribe(() => this.recargar())
    );
    // Buscador de contactos (dropdown) con debounce contra contacto-api.
    this.subscription.add(
      this.contactSearch$.pipe(debounceTime(300)).subscribe(term => this.ejecutarBusquedaContacto(term))
    );
    // Tras enviar un correo desde el compose global → aparece en enviados, refrescar.
    this.subscription.add(
      this.emailCompose.sent$.subscribe(() => this.onCorreoEnviado())
    );
    // Cache-first: muestra de inmediato el timeline cacheado y dispara el sync
    // del buzón IMAP en paralelo (sin bloquear).
    this.cargar(true);
    this.dispararSync();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.revokeBodyFrame();
  }

  // ── Filtros ────────────────────────────────────────────────────────

  onFiltroTexto(): void {
    this.searchSubject.next();
  }

  setDirection(dir: DirectionFilter): void {
    if (this.directionFilter === dir) return;
    this.directionFilter = dir;
    // El "solo no leídos" no aplica a enviados.
    if (dir === 'SENT') this.soloNoLeidos = false;
    this.recargar();
  }

  toggleNoLeidos(): void {
    if (this.directionFilter === 'SENT') return;
    this.soloNoLeidos = !this.soloNoLeidos;
    this.recargar();
  }

  /** El toggle de no-leídos solo tiene sentido con recibidos. */
  get noLeidosVisible(): boolean {
    return this.directionFilter !== 'SENT';
  }

  // ── Filtro "Conversación con" ──────────────────────────────────────

  get convActiva(): boolean {
    return this.convContactId !== null || !!this.convEmail;
  }

  /** Acción rápida desde un item/detalle: ver todo lo conversado con la contraparte. */
  verConversacionItem(item: InboxItem, event?: Event): void {
    event?.stopPropagation();
    const email = item.counterpartEmail?.trim();
    const label = item.counterpartName?.trim() || email;
    // El item del timeline no trae el contactId de la contraparte → usamos email.
    if (!email) {
      this.toast.error({ title: 'Sin correo', message: 'Este correo no tiene una dirección de contraparte para filtrar.' });
      return;
    }
    this.aplicarConversacion(null, email, label || email);
  }

  /** Filtro por correo libre escrito en el buscador de contactos. */
  filtrarPorCorreoLibre(): void {
    const email = this.contactQuery.trim();
    if (!email) return;
    this.aplicarConversacion(null, email, email);
    this.cerrarBuscadorContacto();
  }

  /** Selección de un contacto del dropdown. */
  seleccionarContacto(c: Contact): void {
    const label = c.fullname?.trim() || c.mail;
    // Si por alguna razón el contacto no trae id, caemos al filtro por correo.
    if (c.id != null) {
      this.aplicarConversacion(c.id, null, label);
    } else if (c.mail) {
      this.aplicarConversacion(null, c.mail, label);
    }
    this.cerrarBuscadorContacto();
  }

  private aplicarConversacion(contactId: number | null, email: string | null, label: string): void {
    this.convContactId = contactId;
    this.convEmail = email;
    this.convLabel = label;
    this.recargar();
  }

  limpiarConversacion(): void {
    this.convContactId = null;
    this.convEmail = null;
    this.convLabel = null;
    this.recargar();
  }

  // ── Buscador de contactos (dropdown) ──

  toggleContactSearch(): void {
    this.showContactSearch = !this.showContactSearch;
    if (this.showContactSearch) {
      this.contactQuery = '';
      this.contactResults = [];
    }
  }

  cerrarBuscadorContacto(): void {
    this.showContactSearch = false;
    this.contactQuery = '';
    this.contactResults = [];
  }

  onContactQueryChange(): void {
    this.contactSearch$.next(this.contactQuery.trim());
  }

  private ejecutarBusquedaContacto(term: string): void {
    if (term.length < 2) {
      this.contactResults = [];
      this.contactSearching = false;
      return;
    }
    const businessId = this.authService.getCurrentUser()?.businessId;
    if (businessId === undefined || businessId === null) {
      this.contactResults = [];
      return;
    }
    this.contactSearching = true;
    this.contactoApi.searchContacts({ businessId, searchTerm: term, page: 0, size: 8 }).subscribe({
      next: (res) => {
        this.contactResults = res.content;
        this.contactSearching = false;
      },
      error: () => {
        this.contactResults = [];
        this.contactSearching = false;
      }
    });
  }

  // ── Carga ──────────────────────────────────────────────────────────

  recargar(): void {
    this.page = 0;
    this.last = false;
    this.cargar(true);
  }

  cargarMas(): void {
    if (this.loadingMore || this.loading || this.last) return;
    this.page += 1;
    this.cargar(false);
  }

  /** Hay más páginas si la última respuesta no era la final. */
  get puedeCargarMas(): boolean {
    return !this.last && this.items.length > 0;
  }

  /**
   * Carga una página del timeline global. En `silent` no muestra el spinner
   * grande (recargas en segundo plano tras un sync con novedades).
   */
  private cargar(reset: boolean, silent = false): void {
    if (reset) {
      if (!silent) this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.errorMessage = '';

    const params: MyTimelineSearchParams = { page: reset ? 0 : this.page, size: PAGE_SIZE };
    const q = this.filtroQ.trim();
    if (q) params.q = q;
    if (this.directionFilter === 'RECEIVED') params.direction = 'RECEIVED';
    if (this.directionFilter === 'SENT') params.direction = 'SENT';
    if (this.soloNoLeidos) params.unreadOnly = true;
    if (this.convContactId !== null) params.contactId = this.convContactId;
    if (this.convEmail) params.email = this.convEmail;

    this.emailApi.getMineTimeline(params).subscribe({
      next: (res) => {
        const nuevos = res.content.map(it => this.fromTimeline(it));
        this.items = reset ? nuevos : [...this.items, ...nuevos];
        this.page = res.page ?? this.page;
        this.last = !res.hasNext;
        this.loading = false;
        this.loadingMore = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadingMore = false;
        const msg = err?.error?.message || 'Error al cargar la bandeja de entrada';
        if (!silent) {
          this.errorMessage = msg;
          this.toast.error({ title: 'Error', message: msg });
        }
      }
    });
  }

  // ── Sync IMAP on-demand (cache-first) ─────────────────────────────

  refrescar(): void {
    this.recargar();
    this.dispararSync();
  }

  private dispararSync(): void {
    if (this.syncing) return;
    this.syncing = true;

    this.emailApi.syncMyInbox().subscribe({
      next: (res: ContactInboxSyncResponse) => {
        this.syncing = false;
        this.manejarResultadoSync(res);
      },
      error: () => {
        // Error HTTP real (401/403/...): no rompemos la bandeja (ya muestra lo
        // cacheado). Aviso discreto.
        this.syncing = false;
        this.toast.error({ title: 'Error', message: 'No se pudo actualizar la bandeja' });
      }
    });
  }

  private manejarResultadoSync(res: ContactInboxSyncResponse): void {
    // Solo recargar si hubo recibidos nuevos.
    if (res.synced && res.newMessages > 0) {
      this.noInbox = false;
      this.page = 0;
      this.last = false;
      this.cargar(true, true); // recarga silenciosa (ya hay lista visible)
      return;
    }

    switch (res.reason) {
      case 'NO_IMAP_CONNECTION':
        // No es error: el timeline igual muestra enviados/recibidos cacheados.
        // Aviso no intrusivo con CTA a configuración.
        this.noInbox = true;
        break;
      case 'SYNC_ERROR':
        this.toast.error({ title: 'Error', message: 'No se pudo actualizar la bandeja' });
        break;
      // COOLDOWN / skipped / null → silencioso.
      default:
        break;
    }
  }

  // ── Mapeo timeline → view-model ───────────────────────────────────

  private fromTimeline(it: TimelineItemResponse): InboxItem {
    return {
      direction: it.direction,
      sourceType: it.sourceType,
      sourceId: it.sourceId,
      key: `${it.sourceType}:${it.sourceId}`,
      fecha: it.occurredAt,
      subject: it.subject,
      counterpartName: it.counterpartName,
      counterpartEmail: it.counterpartEmail,
      read: it.direction === 'RECEIVED' ? it.isRead !== false : true,
      status: it.status ?? null,
      purpose: it.purpose ?? null,
      bulk: it.bulk ?? null
    };
  }

  // ── Detalle ───────────────────────────────────────────────────────

  openItem(item: InboxItem): void {
    if (this.detailLoading && this.selectedKey === item.key) return;
    this.selectedKey = item.key;
    this.selectedDirection = item.direction;
    this.receivedDetail = null;
    this.sentDetail = null;
    this.detailError = '';
    this.detailLoading = true;
    this.revokeBodyFrame();

    if (item.sourceType === 'EMAIL_MESSAGE') {
      this.openSent(item.sourceId);
    } else {
      this.openReceived(item.sourceId);
    }
  }

  private openReceived(id: number): void {
    this.emailApi.getInboxMessageDetail(id).subscribe({
      next: (detail) => {
        this.receivedDetail = detail;
        this.detailLoading = false;
        this.buildBodyFrame(detail.bodyHtml);
      },
      error: (err) => {
        this.detailLoading = false;
        this.detailError = err?.error?.message || 'Error al cargar el correo recibido';
      }
    });
  }

  private openSent(id: number): void {
    this.emailApi.getEmailById(id).subscribe({
      next: (detail) => {
        this.sentDetail = detail;
        this.detailLoading = false;
        this.buildBodyFrame(detail.bodyHtml);
      },
      error: (err) => {
        this.detailLoading = false;
        this.detailError = err?.error?.message || 'Error al cargar el correo enviado';
      }
    });
  }

  cerrarDetalle(): void {
    this.selectedKey = null;
    this.selectedDirection = null;
    this.receivedDetail = null;
    this.sentDetail = null;
    this.detailError = '';
    this.revokeBodyFrame();
  }

  // ── Render seguro del cuerpo HTML (iframe sandbox) — ambas direcciones ──

  private buildBodyFrame(bodyHtml: string | null): void {
    if (!bodyHtml) {
      this.bodyFrameSrc = null;
      return;
    }
    const doc = `<!DOCTYPE html><html><head><meta charset="utf-8">` +
      `<base target="_blank">` +
      `<style>` +
      `html{margin:0;padding:0;}` +
      `body{margin:0;padding:16px 20px;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;` +
      `font-size:14px;line-height:1.5;color:#1f2937;word-break:break-word;overflow-wrap:anywhere;}` +
      `img{max-width:100%;height:auto;}` +
      `a{color:#465fff;}` +
      `table{max-width:100%;}` +
      `</style></head><body>${bodyHtml}</body></html>`;
    const blob = new Blob([doc], { type: 'text/html' });
    this.bodyBlobUrl = URL.createObjectURL(blob);
    this.bodyFrameSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.bodyBlobUrl);
  }

  private revokeBodyFrame(): void {
    if (this.bodyBlobUrl) {
      URL.revokeObjectURL(this.bodyBlobUrl);
      this.bodyBlobUrl = null;
    }
    this.bodyFrameSrc = null;
  }

  // ── Helpers de presentación ───────────────────────────────────────

  /** Etiqueta del otro lado de un item de la lista (remitente o destinatario). */
  counterpartLabel(item: InboxItem): string {
    const name = item.counterpartName?.trim();
    if (name) return name;
    return item.counterpartEmail?.trim() || '(desconocido)';
  }

  counterpartInitials(item: InboxItem): string {
    return this.initialsFrom(this.counterpartLabel(item));
  }

  /** Etiqueta del remitente para el detalle de un recibido. */
  receivedSenderLabel(detail: InboxMessageDetailResponse): string {
    return detail.fromName?.trim() || detail.fromEmail?.trim() || '(desconocido)';
  }

  receivedSenderInitials(detail: InboxMessageDetailResponse): string {
    return this.initialsFrom(this.receivedSenderLabel(detail));
  }

  private initialsFrom(label: string): string {
    const parts = label.replace(/[<>@].*$/, '').trim().split(/\s+/).filter(p => p.length > 0);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  // ── Direcciones del detalle de un recibido (addresses[]) ──

  receivedToAddresses(detail: InboxMessageDetailResponse): string {
    return (detail.addresses || [])
      .filter(a => a.kind === 'TO' && !!a.email)
      .map(a => a.email as string)
      .join(', ');
  }

  receivedCcAddresses(detail: InboxMessageDetailResponse): string {
    return (detail.addresses || [])
      .filter(a => a.kind === 'CC' && !!a.email)
      .map(a => a.email as string)
      .join(', ');
  }

  // ── Destinatarios del detalle de un enviado (recipients[]) ──

  sentRecipientsTo(detail: EmailMessageDetailResponse): string {
    if (!detail.recipients?.length) return '—';
    const tos = detail.recipients.filter(r => r.kind === 'TO');
    const list = tos.length ? tos : detail.recipients;
    return list.map(r => (r.fullName ? `${r.fullName} <${r.email}>` : r.email)).join(', ');
  }

  sentRecipientsCc(detail: EmailMessageDetailResponse): string {
    if (!detail.recipients?.length) return '';
    const ccs = detail.recipients.filter(r => r.kind === 'CC');
    return ccs.map(r => (r.fullName ? `${r.fullName} <${r.email}>` : r.email)).join(', ');
  }

  // ── Badge de estado (enviados) ──

  statusColor(status: string | null): string {
    switch (status) {
      case 'SENT':
      case 'DELIVERED':
        return 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400';
      case 'QUEUED':
      case 'SENDING':
      case 'DEFERRED':
        return 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-orange-400';
      case 'FAILED':
      case 'BOUNCED':
      case 'COMPLAINED':
        return 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-500';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-white/5 dark:text-white/80';
    }
  }

  statusLabel(status: string | null): string {
    if (!status) return '';
    const map: Record<string, string> = {
      QUEUED: 'En cola',
      SENDING: 'Enviando',
      SENT: 'Enviado',
      DELIVERED: 'Entregado',
      FAILED: 'Fallido',
      BOUNCED: 'Rebotado',
      DEFERRED: 'Diferido',
      COMPLAINED: 'Spam',
    };
    return map[status] || status;
  }

  /** Chip de estado en la lista: solo si el enviado NO está completado. */
  showStatusChip(item: InboxItem): boolean {
    return item.direction === 'SENT' && !!item.status && item.status !== 'SENT' && item.status !== 'DELIVERED';
  }

  // ── Personal vs Campaña (solo enviados) ──

  /** true = badge "Campaña" (masivo); false = badge "Personal". null en recibidos. */
  isBulk(bulk: boolean | null): boolean {
    return bulk === true;
  }

  /** Etiqueta corta del badge. */
  envioBadgeLabel(bulk: boolean | null): string {
    return bulk === true ? 'Campaña' : 'Personal';
  }

  /** Estilo del badge personal/campaña (violeta para campaña, gris para personal). */
  envioBadgeColor(bulk: boolean | null): string {
    return bulk === true
      ? 'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300'
      : 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300';
  }

  /** Etiqueta amigable en español del purpose (tooltip/subtítulo). */
  purposeLabel(purpose: string | null, bulk: boolean | null): string {
    if (purpose) {
      const map: Record<string, string> = {
        MARKETING_CAMPAIGN: 'Campaña de marketing',
        CONTACT_INVITATION: 'Invitación masiva',
        CONTACT_MESSAGE: 'Mensaje personal',
        QUOTE_DELIVERY: 'Cotización',
        MEETING_INVITE: 'Invitación a reunión',
        MEETING_REMINDER: 'Recordatorio de reunión',
        FORM_DELIVERY: 'Formulario',
        TEAM_MESSAGE: 'Mensaje de equipo',
      };
      if (map[purpose]) return map[purpose];
    }
    return bulk === true ? 'Campaña' : 'Personal';
  }

  /**
   * El detalle del enviado (EmailMessageDetailResponse) trae `purpose` pero NO
   * `bulk`; derivamos masivo a partir del purpose (campaña / invitación masiva).
   */
  private readonly bulkPurposes = new Set(['MARKETING_CAMPAIGN', 'CONTACT_INVITATION']);

  isBulkPurpose(purpose: string | null | undefined): boolean {
    return !!purpose && this.bulkPurposes.has(purpose);
  }

  /** Acción rápida "Ver conversación" desde el panel de detalle (recibido o enviado). */
  verConversacionDesdeDetalle(): void {
    if (this.receivedDetail) {
      const email = this.receivedDetail.fromEmail?.trim();
      const label = this.receivedDetail.fromName?.trim() || email;
      if (email) this.aplicarConversacion(null, email, label || email);
      return;
    }
    if (this.sentDetail) {
      const to = this.sentDetail.recipients?.find(r => r.kind === 'TO') ?? this.sentDetail.recipients?.[0];
      const email = to?.email?.trim();
      const label = to?.fullName?.trim() || email;
      if (email) this.aplicarConversacion(null, email, label || email);
    }
  }

  /**
   * Responder a un correo RECIBIDO: abre el compose flotante global con el
   * remitente como destinatario y el asunto con prefijo "RE:".
   */
  responderRecibido(): void {
    const d = this.receivedDetail;
    if (!d) return;
    const email = d.fromEmail?.trim();
    if (!email) {
      this.toast.error({ title: 'Sin remitente', message: 'Este correo no tiene una dirección de remitente para responder.' });
      return;
    }
    const options: EmailComposeOptions = {
      email,
      subject: this.conPrefijoRe(d.subject)
    };
    const fullName = d.fromName?.trim();
    if (fullName) options.fullName = fullName;
    if (d.idContact !== null && d.idContact !== undefined) options.contactId = d.idContact;
    this.emailCompose.open(options);
  }

  /**
   * Prefija el asunto con "RE: " sin duplicar: si ya empieza por "RE:"
   * (case-insensitive, con o sin espacio) lo deja igual.
   */
  conPrefijoRe(subject: string | null): string {
    const s = (subject ?? '').trim();
    if (/^re:/i.test(s)) return s;
    return s ? `RE: ${s}` : 'RE:';
  }

  /** Fecha relativa corta para la lista: "Hace 5 min", "Ayer", "26 mar 2026". */
  formatRelative(iso: string | null): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Ahora';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `Hace ${diffHrs} h`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return this.datetimeService.formatDateShort(iso);
  }

  formatFull(iso: string | null): string {
    return this.datetimeService.formatDateTime(iso);
  }
}
